import React, { useEffect, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { Spin, Alert, Card, Typography, Space, Progress, Divider } from 'antd'
import { 
  CarOutlined, 
  EnvironmentOutlined, 
  ClockCircleOutlined, 
  PhoneOutlined,
  UserOutlined,
  HomeOutlined,
  ReloadOutlined
} from '@ant-design/icons'
import { env } from '../config/environment'

const { Title, Text, Paragraph } = Typography

// Use centralized environment config - strips /api suffix for public routes
const API_URL = env.apiBaseUrl.replace(/\/api$/, '')

interface TrackingData {
  technicianId: string
  technicianName: string
  technicianFirstName: string
  technicianAvatar: string | null
  jobId: string
  businessId: string
  businessName: string
  businessPhone: string | null
  serviceAddress: string
  latitude: number | null
  longitude: number | null
  lastLocationUpdate: string | null
  distanceEta: {
    distanceMiles: number
    distanceText: string
    etaMinutes: number
    etaText: string
    isAvailable: boolean
  } | null
}

export const TechnicianTracking: React.FC = () => {
  const { token } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<TrackingData | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())

  const fetchTrackingData = useCallback(async (showRefreshing = false) => {
    if (!token) {
      setError('No tracking link provided')
      setLoading(false)
      return
    }

    if (showRefreshing) {
      setRefreshing(true)
    }

    try {
      const response = await fetch(`${API_URL}/api/public/tracking/${token}`)
      const result = await response.json()

      if (!result.success) {
        setError(result.error || 'Failed to load tracking information')
        setLoading(false)
        setRefreshing(false)
        return
      }

      setData(result.tracking)
      setLastRefresh(new Date())
      setLoading(false)
      setRefreshing(false)
    } catch (err) {
      console.error('Error fetching tracking data:', err)
      setError('Failed to load tracking information. Please try again.')
      setLoading(false)
      setRefreshing(false)
    }
  }, [token])

  // Initial fetch
  useEffect(() => {
    fetchTrackingData()
  }, [fetchTrackingData])

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTrackingData(true)
    }, 30000)

    return () => clearInterval(interval)
  }, [fetchTrackingData])

  const handleRefresh = () => {
    fetchTrackingData(true)
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 24
      }}>
        <Spin size="large" />
        <Text style={{ color: 'white', marginTop: 16 }}>Loading tracking information...</Text>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 24
      }}>
        <Card style={{ maxWidth: 400, width: '100%', textAlign: 'center' }}>
          <Alert 
            type="error" 
            message="Tracking Unavailable" 
            description={error}
            showIcon
          />
        </Card>
      </div>
    )
  }

  if (!data) {
    return null
  }

  // Calculate progress based on ETA (0% at start, 100% when arriving)
  const etaProgress = data.distanceEta?.isAvailable 
    ? Math.max(0, Math.min(100, 100 - (data.distanceEta.etaMinutes / 60) * 100))
    : 0

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '24px 16px'
    }}>
      <div style={{ maxWidth: 500, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24, color: 'white' }}>
          <CarOutlined style={{ fontSize: 48, marginBottom: 8 }} />
          <Title level={2} style={{ color: 'white', margin: 0 }}>
            {data.technicianFirstName} is on the way!
          </Title>
          <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 16 }}>
            {data.businessName}
          </Text>
        </div>

        {/* Main Card */}
        <Card 
          style={{ 
            borderRadius: 16, 
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            marginBottom: 16
          }}
        >
          {/* Technician Info */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            {data.technicianAvatar ? (
              <img 
                src={data.technicianAvatar}
                alt={data.technicianName}
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  margin: '0 auto 12px',
                  display: 'block',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                  border: '3px solid #667eea'
                }}
                onError={(e) => {
                  // Fallback to icon if image fails to load
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                  const fallback = target.nextElementSibling as HTMLElement
                  if (fallback) fallback.style.display = 'flex'
                }}
              />
            ) : null}
            <div style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: data.technicianAvatar ? 'none' : 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
            }}>
              <UserOutlined style={{ fontSize: 36, color: 'white' }} />
            </div>
            <Title level={4} style={{ margin: 0 }}>{data.technicianName}</Title>
            <Text type="secondary">Your Technician</Text>
          </div>

          {/* ETA Section */}
          {data.distanceEta?.isAvailable ? (
            <div style={{ 
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              borderRadius: 12,
              padding: 20,
              marginBottom: 20,
              textAlign: 'center'
            }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <EnvironmentOutlined style={{ fontSize: 20, color: '#0369a1', marginRight: 8 }} />
                  <Text strong style={{ fontSize: 24, color: '#0369a1' }}>
                    {data.distanceEta.distanceText}
                  </Text>
                  <Text type="secondary"> away</Text>
                </div>
                
                <Divider style={{ margin: '12px 0' }} />
                
                <div>
                  <ClockCircleOutlined style={{ fontSize: 20, color: '#0369a1', marginRight: 8 }} />
                  <Text strong style={{ fontSize: 24, color: '#0369a1' }}>
                    {data.distanceEta.etaText}
                  </Text>
                </div>

                <Progress 
                  percent={etaProgress} 
                  showInfo={false}
                  strokeColor={{
                    '0%': '#667eea',
                    '100%': '#764ba2',
                  }}
                  style={{ marginTop: 12 }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Estimated arrival time
                </Text>
              </Space>
            </div>
          ) : (
            <div style={{ 
              background: '#f5f5f5',
              borderRadius: 12,
              padding: 20,
              marginBottom: 20,
              textAlign: 'center'
            }}>
              <Text type="secondary">
                Distance and ETA information is currently unavailable.
                Your technician is on the way!
              </Text>
            </div>
          )}

          {/* Service Location */}
          <div style={{ marginBottom: 20 }}>
            <Space align="start">
              <HomeOutlined style={{ fontSize: 20, color: '#666', marginTop: 2 }} />
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Service Location</Text>
                <Paragraph style={{ margin: 0, fontWeight: 500 }}>
                  {data.serviceAddress || 'Address not available'}
                </Paragraph>
              </div>
            </Space>
          </div>

          {/* Contact Info */}
          {data.businessPhone && (
            <div style={{ 
              background: '#f9fafb',
              borderRadius: 8,
              padding: 16,
              textAlign: 'center'
            }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Questions? Contact us
              </Text>
              <a 
                href={`tel:${data.businessPhone}`}
                style={{ 
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#667eea'
                }}
              >
                <PhoneOutlined />
                {data.businessPhone}
              </a>
            </div>
          )}
        </Card>

        {/* Refresh Button */}
        <Card 
          style={{ 
            borderRadius: 12, 
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          hoverable
          onClick={handleRefresh}
        >
          <Space>
            <ReloadOutlined spin={refreshing} style={{ fontSize: 16 }} />
            <Text>
              {refreshing ? 'Refreshing...' : 'Tap to refresh'}
            </Text>
          </Space>
          <div style={{ marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Last updated: {lastRefresh.toLocaleTimeString()}
            </Text>
          </div>
        </Card>

        {/* Footer Note */}
        <div style={{ 
          textAlign: 'center', 
          marginTop: 24,
          color: 'rgba(255,255,255,0.8)',
          fontSize: 12
        }}>
          <Text style={{ color: 'rgba(255,255,255,0.8)' }}>
            This page auto-refreshes every 30 seconds
          </Text>
          <br />
          <Text style={{ color: 'rgba(255,255,255,0.7)' }}>
            Powered by {data.businessName}
          </Text>
        </div>
      </div>
    </div>
  )
}
